import React, { useEffect, useRef, useState } from "react";
import { CheckIcon } from "lucide-react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";

interface Option {
  id: number | string;
  name: string;
  [key: string]: any; // Allow for additional properties
}

interface GenericDropdownProps<T extends Option> {
  options: T[];
  onSelect: (option: T) => void;
  initialSelectedId?: string;
  renderOption?: (option: T, isSelected: boolean) => React.ReactNode;
  buttonClassName?: string;
  dropdownClassName?: string;
  label?: string;
  compact?: boolean;
}

function GenericDropdown<T extends Option>({
  options,
  onSelect,
  initialSelectedId,
  renderOption,
  buttonClassName = "",
  dropdownClassName = "",
  label,
  compact = false,
}: GenericDropdownProps<T>) {
  const [selected, setSelected] = useState<T>(
    () => options.find((opt) => opt.id === initialSelectedId) || options[0]
  );
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update selected when initialSelectedId changes
  useEffect(() => {
    if (initialSelectedId) {
      const newSelected = options.find((opt) => opt.id === initialSelectedId);
      if (newSelected && newSelected.id !== selected.id) {
        setSelected(newSelected);
      }
    } else if (options.length > 0 && options[0].id !== selected.id) {
      setSelected(options[0]);
    }
  }, [initialSelectedId, options, selected.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelection = (option: T) => {
    setSelected(option);
    setIsOpen(false);
    onSelect(option);
  };

  return (
    <div className={buttonClassName}>
      {label && (
        <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`${compact ? "h-10" : "h-11"} cursor-pointer ${
            compact ? "rounded-lg" : "rounded-xl"
          } bg-white dark:bg-gray-800 py-1.5 ${
            compact ? "pl-3 pr-8" : "pl-3 pr-10"
          } text-left text-gray-900 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-primary ${
            compact ? "text-sm" : "sm:text-sm"
          } sm:leading-6 ${buttonClassName}`}
        >
          <span
            className={`block truncate ${
              compact ? "font-medium text-sm" : "font-semibold"
            }`}
          >
            {selected.name}
          </span>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </span>
        </button>

        {isOpen && (
          <ul
            className={`absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-[#1F2C33] py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600 dark:border dark:border-gray-600 focus:outline-none sm:text-sm ${dropdownClassName}`}
          >
          {options.map((option, index) => (
            <li
              key={option.id}
              onClick={() => handleSelection(option)}
              className={`group relative min-w-28 items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 select-none py-2 pl-3 pr-9 text-gray-900 dark:text-white ${
                selected.id === option.id
                  ? "bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                  : ""
              } ${
                index !== options.length - 1 ? "border-b border-gray-100 dark:border-gray-700" : ""
              }`}
            >
              {renderOption ? (
                renderOption(option, selected.id === option.id)
              ) : (
                <div>
                  <span
                    className={`block truncate font-normal ${
                      selected.id === option.id ? "font-semibold" : ""
                    }`}
                  >
                    {option.name}
                  </span>
                  {selected.id === option.id && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-600 dark:text-gray-300">
                      <CheckIcon className="h-5 w-5" />
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default GenericDropdown;
